import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setInvoiceFilterPeriod,
  getReconciliationInfoRequest,
  getReconciliationListRequest,
  setSelectedReconciliationId,
  getSelectedReconciliationDetailsRequest,
  clearSelectedReconciliation,
} from "../../../redux/actions/reconciliation";
import { convertNumberToFrontend } from "../../../helpers/convert";
import Button from "../../../components/Button";
import { Select, Textarea } from "../../../components/Inputs";
import DataTable from "../../../components/DataTable";
import Loading from "../../../components/Loading";
import Modal from "../../../components/Modal";
import moment from "moment";
import { API_EXPORT, API_POST } from "../../../helpers/api";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import { setNotification } from "../../../helpers/notification";
import fileDownload from "js-file-download";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

const Invoicing = ({ t }) => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.reconciliation.info);
  const filterPeriod = useSelector(
    (state) => state.reconciliation.invoiceFilterPeriod
  );
  const {
    reconciliationList,
    total,
    dueDate,
    invoiceDate,
    subTotalsInPage,
    subTotalsInSystem,
  } = useSelector((state) => state.reconciliation);

  const loading = useSelector((state) => state.reconciliation.loading);
  const { selectedId, loadingDetails, details } = useSelector(
    (state) => state.reconciliation.selectedReconciliation
  );

  const [filterPeriodOptions, setFilterPeriodOptions] = useState([]);
  const [filteredPeriod, setFilteredPeriod] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [showErrorReportingModal, setShowErrorReportingModal] = useState(false);
  const [description, setDescription] = useState("");
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [modalConfirmationDetails, setModalConfirmationDetails] = useState({
    header: "",
    body: "",
    approve: null,
  });
  const [detailsConfig, setDetailsConfig] = useState([]);
  const [invoicingExportLoading, setInvoicingExportLoading] = useState(false);
  const [invoicingDetailsExportLoading, setInvoicingDetailsExportLoading] =
    useState(false);
  const [downloadFilter, setDownloadFilter] = useState({ term: filterPeriod });
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);
  const detailConstants = [
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "ALIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "SATIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "PIU",
      title: "PIU",
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "GZ",
      title: t("pages.default.reconciliation.table.fee"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "DK",
      title: "DK",
    },
  ];
  const typeTitles = {
    ALIS: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    SATIS: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    PIU: "PIU",
    GZ: t("pages.default.reconciliation.table.fee"),
    DK: "DK",
  };
  const statuses = {
    FINANCE: "FINANCE",
    NONE: "NONE",
    ERROR: "ERROR",
    TAKASBANK: "TAKASBANK",
    INVOICE: "INVOICE",
  };

  const invoicingColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.invoicing.table.ENTSOCode"),
      selector: "ENTSOCode",
      sortable: false,
      cell: (row) => (row.organizationENSTO ? row.organizationENSTO : ""),
    },
    {
      name: t("pages.default.reconciliation.table.totalCredit"),
      selector: "totalCredit",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.totalCredit, digit: 2 });
      },
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.totalCreditKDVAmount"),
      selector: "totalCreditKDV",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({
          num: row.totalCredit * 1.2,
          digit: 2,
        });
      },
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.totalDebit"),
      selector: "totalDebit",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.totalDebit, digit: 2 });
      },
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.totalDebitKDVAmount"),
      selector: "totalDebitKDV",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({
          num: row.totalDebit * 1.2,
          digit: 2,
        });
      },
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
  ];

  const invoicingDetailsColumns = [
    {
      name: t("pages.default.reconciliation.table.itemName"),
      selector: "type",
      sortable: true,
      cell: (row) => typeTitles[row.type],
    },
    {
      name: t("pages.default.reconciliation.table.credit"),
      selector: "totalCredit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({ num: row.totalCredit, digit: 2 })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.creditKDV"),
      selector: "totalCreditKDV",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalCredit * 1.2 - row.totalCredit),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.total"),
      selector: "sumCredit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalCredit * 1.2),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.debit"),
      selector: "totalDebit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({ num: row.totalDebit, digit: 2 })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.debitKDV"),
      selector: "totalDebitKDV",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalDebit * 1.2 - row.totalDebit),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.total"),
      selector: "sumDebit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalDebit * 1.2),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
  ];

  useEffect(() => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    setFilteredPeriod(filterPeriod);
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // term :'' has to be changed
    dispatch(
      getReconciliationListRequest({
        term: filterPeriod,
        operation: "INVOICE",
      })
    );

    return () => {
      dispatch(clearSelectedReconciliation());
    };
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // set Period options
    let d = new Date();
    let filterPeriods = [];
    for (let i = -2; i <= 12; i++) {
      const name =
        t(`ui.default.months.${moment().subtract(i, "month").format("M")}`) +
        " " +
        moment().subtract(i, "month").format("YYYY");
      filterPeriods.push({
        name,
        value: moment().subtract(i, "month").format("YYYYMM"),
      });
      d.setMonth(d.getMonth() - 1);
    }
    setFilterPeriodOptions(filterPeriods);
  }, [t]);

  useEffect(() => {
    handleCreateInvoicingDetailsTable();
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [details]);

  const handlePeriodChange = (e) => {
    const { value } = e.target;
    dispatch(setInvoiceFilterPeriod(value));
  };

  const filterReconciliationList = () => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    dispatch(
      getReconciliationListRequest({
        term: filterPeriod,
        operation: "INVOICE",
      })
    );
    setFilteredPeriod(filterPeriod);
    setDownloadFilter({ term: filterPeriod });
    (selectedId || details.length !== 0) &&
      dispatch(clearSelectedReconciliation());
  };

  const handleSelectReconciliation = (row) => {
    dispatch(setSelectedReconciliationId(row.organizationId));
    // term :'' has to be changed
    dispatch(
      getSelectedReconciliationDetailsRequest({
        organizationId: row.organizationId,
        term: filteredPeriod,
        operation: "INVOICE",
      })
    );
  };

  const handleSelectRow = (row) =>
    selectedId && selectedId === row.organizationId;

  const handleCreateInvoicingDetailsTable = () => {
    let updatedDetails = [];
    for (let data of detailConstants) {
      if (!(data.type === typeTitles.DK && status !== statuses.INVOICE)) {
        const foundDetail =
          details && details?.find((detail) => data.type === detail.type);
        if (foundDetail) {
          updatedDetails.push({
            ...foundDetail,
          });
        } else {
          updatedDetails.push({
            ...data,
            totalCredit: 0,
            totalDebit: 0,
          });
        }
      }
    }
    setDetailsConfig(updatedDetails);
  };

  const handleSaveClick = async () => {
    setLoadingRequest(true);
    try {
      await API_POST("reconciliation/sendToReconciliationWithError", {
        description,
      });
      setLoadingRequest(false);
      setShowErrorReportingModal(false);
      setNotification(
        "success",
        t("pages.default.invoicing.reportError.success")
      );
      dispatch(getReconciliationInfoRequest(filteredPeriod));
      selectedId && dispatch(clearSelectedReconciliation());
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleSetModalConfirmationDetails = (type) => {
    setShowModalConfirmation(true);
    switch (type) {
      case "SEND_TO_TAKASBANK":
        setModalConfirmationDetails({
          header: t("pages.default.invoicing.sendToTakasbank.title"),
          body: t("pages.default.invoicing.sendToTakasbank.body"),
          approve: handleSendToTakasbankClick,
        });
        break;
      case "SEND_TO_RECONCILIATION":
        setModalConfirmationDetails({
          header: t("pages.default.invoicing.sendToReconciliation.title"),
          body: t("pages.default.invoicing.sendToReconciliation.body"),
          approve: handleSendToReconciliationClick,
        });
        break;
      default:
        break;
    }
  };

  const handleSendToTakasbankClick = async () => {
    setLoadingRequest(true);
    try {
      await API_POST("reconciliation/sendToTakasbank");
      setLoadingRequest(false);
      setShowModalConfirmation(false);
      setNotification(
        "success",
        t("pages.default.invoicing.sendToTakasbank.success")
      );
      dispatch(getReconciliationInfoRequest(filteredPeriod));
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleSendToReconciliationClick = async () => {
    setLoadingRequest(true);
    try {
      await API_POST("reconciliation/sendToReconciliation");
      setLoadingRequest(false);
      setShowModalConfirmation(false);
      setNotification(
        "success",
        t("pages.default.invoicing.sendToReconciliation.success")
      );
      dispatch(getReconciliationInfoRequest(filteredPeriod));
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleRoundNumber = (num) =>
    Math.round((num + Number.EPSILON) * 100) / 100;

  const downloadInvoicing = async () => {
    setInvoicingExportLoading(true);
    try {
      const res = await API_EXPORT("invoice/export/excel", {
        ...downloadFilter,
        operation: "INVOICE",
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setInvoicingExportLoading(false);
      setNotification("success", t("pages.default.invoicing.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setInvoicingExportLoading(false);
    }
  };

  const downloadInvoicingDetails = async () => {
    setInvoicingDetailsExportLoading(true);
    try {
      const res = await API_EXPORT("invoice/export/excel", {
        ...downloadFilter,
        organizationId: selectedId,
        operation: "INVOICE",
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setInvoicingDetailsExportLoading(false);
      setNotification("success", t("pages.default.invoicing.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setInvoicingDetailsExportLoading(false);
    }
  };

  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getReconciliationListRequest({
        term: filterPeriod,
        operation: "INVOICE",
        page: activePage,
        selector,
        direction,
      })
    );
  };
  return (
    <>
      {status === statuses.INVOICE && (
        <div className="km__p--reconciliation-msg km__p--reconciliation-success">
          {t("pages.default.invoicing.invoicingFinishedMsg")}
        </div>
      )}
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.invoicing.title")}
      </h5>

      <div className="km__p--card">
        <div className="km__p--reconciliation-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.invoicing.title")}
          </div>
          <div className="km__u--text_title">
            {t("pages.default.invoicing.invoiceDate")}

            {moment(invoiceDate).format("DD.MM.YYYY")}
          </div>

          <div className="km__u--text_title">
            {t("pages.default.invoicing.dueDate")}

            {moment(dueDate).format("DD.MM.YYYY")}
          </div>
          <div className="km__p--reconciliation-filters">
            <Select
              options={filterPeriodOptions}
              name="productionPeriod"
              value={filterPeriod}
              onChange={handlePeriodChange}
              id="in-productionPeriod"
            />
            <Button
              label={t("ui.default.filter")}
              onClick={filterReconciliationList}
              id="in-filter"
            />
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadInvoicing}
              disabled={invoicingExportLoading}
              id="in-download"
            />
          </div>
        </div>

        {status !== statuses.NONE && status !== statuses.ERROR && (
          <>
            {!loading && (
              <>
                <DataTableServerSideRendering
                  columns={invoicingColumns}
                  data={reconciliationList}
                  rowKey="id"
                  sortedColumn={sortedColumn}
                  setSortedColumn={setSortedColumn}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                  pagination
                  activePage={activePage}
                  totalPageCount={total?.totalPage}
                  handleFetchData={handleClick}
                  selectableRow
                  rowSelection={(row) => handleSelectReconciliation(row)}
                  isRowSelected={(row) => handleSelectRow(row)}
                  // subTotalsInPage,
                  // subTotalsInSystem,
                  subTotals={[
                    {
                      totalCount: subTotalsInSystem
                        ? convertNumberToFrontend({
                            num: subTotalsInSystem.totalCredit,
                            digit: 2,
                          })
                        : 0,
                      countInPage: subTotalsInPage
                        ? convertNumberToFrontend({
                            num: subTotalsInPage.totalCredit,
                            digit: 2,
                          })
                        : 0,

                      sumIndex: 2,
                      class: "sub-total-left",
                    },
                    {
                      totalCount: subTotalsInSystem
                        ? convertNumberToFrontend({
                            num: subTotalsInSystem.totalCreditWithWat,
                            digit: 2,
                          })
                        : 0,
                      countInPage: subTotalsInPage
                        ? convertNumberToFrontend({
                            num: subTotalsInPage.totalCreditWithWat,
                            digit: 2,
                          })
                        : 0,

                      sumIndex: 3,
                      class: "sub-total-left",
                    },
                    {
                      totalCount: subTotalsInSystem
                        ? convertNumberToFrontend({
                            num: subTotalsInSystem.totalDebit,
                            digit: 2,
                          })
                        : 0,
                      countInPage: subTotalsInPage
                        ? convertNumberToFrontend({
                            num: subTotalsInPage.totalDebit,
                            digit: 2,
                          })
                        : 0,

                      sumIndex: 4,
                      class: "sub-total-left",
                    },
                    {
                      totalCount: subTotalsInSystem
                        ? convertNumberToFrontend({
                            num: subTotalsInSystem.totalDebitWithWat,
                            digit: 2,
                          })
                        : 0,
                      countInPage: subTotalsInPage
                        ? convertNumberToFrontend({
                            num: subTotalsInPage.totalDebitWithWat,
                            digit: 2,
                          })
                        : 0,

                      sumIndex: 5,
                      class: "sub-total-left",
                    },
                  ]}
                />
                {status !== statuses.INVOICE && (
                  <div className="km__p--reconciliation-button-group">
                    <Button
                      type="destructive"
                      label={t("pages.default.invoicing.reportError.title")}
                      onClick={() => setShowErrorReportingModal(true)}
                      disabled={status !== statuses.FINANCE}
                      id="in-reportError"
                    />
                    {status === statuses.TAKASBANK ? (
                      <Button
                        type="secondary"
                        label={t(
                          "pages.default.invoicing.sendToReconciliation.title"
                        )}
                        onClick={() =>
                          handleSetModalConfirmationDetails(
                            "SEND_TO_RECONCILIATION"
                          )
                        }
                        id="in-sendToReconciliation"
                      />
                    ) : (
                      <Button
                        type="primary"
                        label={t(
                          "pages.default.invoicing.sendToTakasbank.title"
                        )}
                        onClick={() =>
                          handleSetModalConfirmationDetails("SEND_TO_TAKASBANK")
                        }
                        disabled={status !== statuses.FINANCE}
                        id="in-sendToTakasbank"
                      />
                    )}
                  </div>
                )}
              </>
            )}
            {loading && (
              <Loading text={t("pages.default.invoicing.invoicingLoading")} />
            )}
          </>
        )}
      </div>

      {selectedId ? (
        <div className="km__p--card">
          <div className="km__p--reconciliation-filter-container">
            <div className="km__u--text_title">
              {t("pages.default.invoicing.detailsTitle")}
            </div>
            <div className="km__p--reconciliation-filters">
              <Button
                type="secondary"
                label={t("ui.default.download")}
                suffixIcon="download"
                onClick={downloadInvoicingDetails}
                disabled={invoicingDetailsExportLoading}
                id="in-detail-download"
              />
            </div>
          </div>
          {!loadingDetails && (
            <DataTable
              columns={invoicingDetailsColumns}
              data={detailsConfig}
              rowKey="type"
            />
          )}
          {loadingDetails && (
            <Loading
              text={t("pages.default.invoicing.invoicingDetailsLoading")}
            />
          )}
        </div>
      ) : null}

      <Modal
        isOpen={showErrorReportingModal}
        toggle={() => setShowErrorReportingModal(false)}
      >
        <div className="km__p--invoicing-error-reporting-modal">
          <div className="km__p--invoicing-error-reporting-modal-header">
            {t("pages.default.invoicing.errorModalTitle")}
          </div>
          <div className="km__p--invoicing-error-reporting-modal-body">
            <Textarea
              label={t("pages.default.reconciliation.inputs.description")}
              name="description"
              minLength={10}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              id="in-modal-description"
            />
          </div>
          <div className="km__p--invoicing-error-reporting-modal-footer">
            <Button
              label={t("ui.default.close")}
              type="secondary"
              onClick={() => setShowErrorReportingModal(false)}
              disabled={loadingRequest}
              id="in-modal-close"
            />
            <Button
              label={t("ui.default.save")}
              type="primary"
              onClick={() => handleSaveClick()}
              disabled={description.trim().length < 10 || loadingRequest}
              id="in-modal-save"
            />
          </div>
        </div>
      </Modal>

      <ModalConfirmation
        isOpen={showModalConfirmation}
        toggle={() => setShowModalConfirmation(false)}
        {...modalConfirmationDetails}
        loading={loadingRequest}
      />
    </>
  );
};

export default Invoicing;
